import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { Loader2, Trash2 } from "lucide-react";

const ALL_TIMES = ["09:00","09:30","10:00","10:30","11:00","11:30","14:00","14:30","15:00","15:30","16:00","16:30","17:00"];

function toIso(date) {
  const d = new Date(date);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

export default function Availability() {
  const { user } = useAuth();
  const [date, setDate] = useState(new Date());
  const [slots, setSlots] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const iso = toIso(date);
  const load = async () => {
    if (!user?.doctor_id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/doctors/${user.doctor_id}/slots`, { params: { date_from: iso, date_to: iso } });
      setSlots(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); setPicked(new Set()); /* eslint-disable-next-line */ }, [iso]);

  const existing = new Set(slots.map((s) => s.start_time));

  const toggle = (t) => {
    if (existing.has(t)) return;
    const n = new Set(picked);
    n.has(t) ? n.delete(t) : n.add(t);
    setPicked(n);
  };

  const save = async () => {
    if (picked.size === 0) return;
    setSaving(true);
    try {
      await api.post("/availability/bulk", { date: iso, times: Array.from(picked) });
      toast.success("Slots added");
      setPicked(new Set()); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (slot) => {
    try { await api.delete(`/availability/${slot.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-slate-900">Availability</h1>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="doccare-card p-6">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Pick date</p>
          <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} disabled={{ before: new Date() }} className="rounded-xl" />
        </div>
        <div className="doccare-card p-6">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Time slots ({iso})</p>
          {loading ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : (
            <>
              <div className="grid grid-cols-4 gap-2">
                {ALL_TIMES.map((t) => {
                  const isExisting = existing.has(t);
                  const isPicked = picked.has(t);
                  return (
                    <button key={t} onClick={() => toggle(t)} disabled={isExisting}
                      className={`px-2 py-2 rounded-lg text-sm font-semibold border transition-colors
                        ${isExisting ? "bg-emerald-100 text-emerald-800 border-emerald-200 cursor-default"
                        : isPicked ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}
                      data-testid={`time-${t}`}
                    >{t}</button>
                  );
                })}
              </div>
              <Button className="mt-4 rounded-xl w-full" onClick={save} disabled={picked.size === 0 || saving} data-testid="save-slots">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : `Add ${picked.size} slot${picked.size === 1 ? "" : "s"}`}
              </Button>

              {slots.length > 0 && (
                <div className="mt-5 pt-5 border-t border-slate-200">
                  <p className="text-xs uppercase font-semibold text-slate-500 mb-2">Existing</p>
                  <div className="space-y-1.5">
                    {slots.map((s) => (
                      <div key={s.id} className="flex items-center justify-between text-sm bg-slate-50 rounded-lg px-3 py-2">
                        <span>{s.start_time} – {s.end_time} <span className="ml-2 text-xs text-slate-500">({s.status})</span></span>
                        {s.status === "available" && (
                          <button onClick={() => remove(s)} className="text-red-500 hover:text-red-700" data-testid={`del-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
